/**
 * The clients register.
 *
 * The reference implementation of a Connect list page, and worth reading before
 * writing another: the columns are declared, the fetchers are built from the
 * endpoint path, and everything else — paging, sorting, the eleven filter
 * modes, facet counts, column arrangement, selection, bulk actions, row detail,
 * the URL round trip — is the table's.
 *
 * A page like this should be a column list and a form. Anything more means
 * something belongs in `ServerTable` instead.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, Globe, Mail, MapPin, Plus, Tag } from 'lucide-react';
import {
  ApiError,
  Button,
  Can,
  PageHeader,
  ServerTable,
  StatusChip,
  api,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  formatMoney,
  toast,
  type ServerColumn,
  type Tone,
} from '@connect/web-shared';

import { ClientFormDialog } from './client-form-dialog';

export interface ClientRow {
  public_id: string;
  code: string;
  name: string;
  trading_name: string | null;
  status: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  country: string | null;
  account_manager_name: string | null;
  department_name: string | null;
  currency: string;
  default_hourly_rate: string | null;
  payment_terms_days: number;
  portal_enabled: boolean;
  contact_count: number;
  onboarded_on: string | null;
  created_at: string;
}

interface ClientOptions {
  statuses: { value: string; label: string }[];
}

/**
 * Status tones.
 *
 * Declared by the page rather than guessed from the value: `on_hold` is a
 * warning for a client relationship and would be neutral for a project, and a
 * shared component deciding for both would be wrong for one of them.
 */
const STATUS_TONES: Record<string, Tone> = {
  prospect: 'info',
  active: 'success',
  on_hold: 'warning',
  former: 'neutral',
};

const ENDPOINT = '/crm/clients';

export function ClientsPage() {
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [recentKey, setRecentKey] = useState<string | null>(null);

  // The option lists come from the server so that adding a status is a backend
  // change alone. A duplicated list in the client is a list that drifts.
  const options = useQuery({
    queryKey: ['crm', 'clients', 'options'],
    queryFn: () => api.get<ClientOptions>(`${ENDPOINT}/options`),
    staleTime: 10 * 60_000,
  });

  const fetchRows = useMemo(() => createServerFetcher<ClientRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const columns = useMemo<ServerColumn<ClientRow>[]>(
    () => [
      {
        field: 'code',
        header: 'Code',
        filter: 'text',
        width: 120,
        render: (row) => <span className="font-mono text-xs text-muted">{row.code}</span>,
      },
      {
        field: 'name',
        header: 'Client',
        icon: <Building2 className="size-3" />,
        filter: 'text',
        minWidth: 200,
        render: (row) => (
          <span className="flex flex-col">
            <span className="truncate font-medium">{row.name}</span>
            {/* The trading name only when it differs — repeating the legal name
                underneath itself is noise in every row. */}
            {row.trading_name && row.trading_name !== row.name ? (
              <span className="truncate text-2xs text-faint">{row.trading_name}</span>
            ) : null}
          </span>
        ),
      },
      {
        field: 'status',
        header: 'Status',
        icon: <Tag className="size-3" />,
        filter: 'select',
        options: options.data?.statuses,
        width: 120,
        render: (row) => <StatusChip value={row.status} tones={STATUS_TONES} />,
      },
      {
        field: 'account_manager_name',
        header: 'Account manager',
        filter: 'select',
        minWidth: 160,
      },
      {
        field: 'city',
        header: 'City',
        icon: <MapPin className="size-3" />,
        filter: 'select',
        width: 130,
      },
      {
        field: 'contact_count',
        header: 'Contacts',
        numeric: true,
        width: 100,
        // Not filterable: it is a computed subquery, so it is not in the
        // endpoint's whitelist and a filter here would be silently ignored.
        sortable: false,
      },
      {
        field: 'default_hourly_rate',
        header: 'Rate',
        filter: 'number',
        numeric: true,
        width: 120,
        // Money arrives as a string and is formatted, never parsed for
        // arithmetic — see `formatMoney`.
        format: (value, row) => formatMoney(value as string | null, row.currency),
      },
      {
        field: 'payment_terms_days',
        header: 'Terms',
        filter: 'number',
        numeric: true,
        width: 90,
        format: (value) => (value ? `${String(value)}d` : '—'),
      },
      {
        field: 'email',
        header: 'Email',
        icon: <Mail className="size-3" />,
        // Exact-match only on the server, which refuses an inexact filter here
        // — a list total would otherwise reconstruct an address one character
        // at a time.
        filter: 'text',
        minWidth: 180,
        hiddenByDefault: true,
      },
      {
        field: 'country',
        header: 'Country',
        icon: <Globe className="size-3" />,
        filter: 'select',
        width: 100,
        hiddenByDefault: true,
      },
      {
        field: 'portal_enabled',
        header: 'Portal',
        filter: 'boolean',
        align: 'center',
        width: 90,
        hiddenByDefault: true,
        render: (row) =>
          row.portal_enabled ? (
            <StatusChip value="enabled" tones={{ enabled: 'success' }} />
          ) : (
            <span className="text-faint">—</span>
          ),
      },
      {
        field: 'onboarded_on',
        header: 'Onboarded',
        filter: 'date',
        width: 130,
        hiddenByDefault: true,
        format: (value) => formatDate(value as string | null),
      },
      {
        field: 'created_at',
        header: 'Added',
        filter: 'date',
        width: 130,
        format: (value) => formatDate(value as string | null),
      },
    ],
    [options.data?.statuses],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Clients"
        description="The organizations you work for."
        actions={
          <Can permission="crm.client.create">
            <Button size="sm" onClick={() => setFormOpen(true)}>
              <Plus className="size-3.5" />
              New client
            </Button>
          </Can>
        }
      />

      <ServerTable<ClientRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['crm', 'clients']}
        stateKey="crm.clients"
        searchPlaceholder="Search by name, code or city"
        viewField="name"
        onView={(row) => navigate(`/clients/${row.public_id}`)}
        recentKey={recentKey}
        selectable
        onDelete={async (row) => {
          try {
            await api.delete(`${ENDPOINT}/${row.public_id}`);
            toast.success(`${row.name} deleted`);
          } catch (error) {
            // The API writes a message a person can act on — "still has
            // contacts", naming what is in the way — so it is shown verbatim.
            toast.error(
              'Could not delete',
              error instanceof ApiError ? error.message : undefined,
            );
            throw error;
          }
        }}
        deleteLabel={(row) => row.name}
        rowDetailFields={(row) => [
          { label: 'Trading name', value: row.trading_name },
          { label: 'Email', value: row.email },
          { label: 'Phone', value: row.phone },
          { label: 'Country', value: row.country },
          { label: 'Department', value: row.department_name },
          { label: 'Currency', value: row.currency },
          { label: 'Payment terms', value: `${row.payment_terms_days} days` },
          { label: 'Onboarded', value: formatDate(row.onboarded_on) },
        ]}
        emptyTitle="No clients yet"
        emptyDescription="A client is an organization you do work for. Add the first one to start recording projects and invoices against it."
        emptyAction={
          <Can permission="crm.client.create">
            <Button size="sm" onClick={() => setFormOpen(true)}>
              <Plus className="size-3.5" />
              Add a client
            </Button>
          </Can>
        }
      />

      <ClientFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={(publicId) => {
          // Flashed and scrolled to, so the new record is visibly where it
          // landed rather than a toast about a row on page four.
          setRecentKey(publicId);
          setFormOpen(false);
        }}
      />
    </div>
  );
}
